import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TransliterationCandidate } from '@shared/nepali/engine';
import { cn } from '@/lib/utils';
import { lang, n, t } from '@/lib/i18n';

/**
 * The other readings of the word just converted.
 *
 * Opened on demand — Ctrl+Space, or a click on the word — and never by itself.
 * Romanized Nepali is ambiguous often enough that a popup on every space press
 * would be unusable in long-form work, and the whole point of the transliterator
 * is that a writer who is getting what they want never has to look at it.
 */

export interface AlternatesAnchor {
  /** Viewport coordinates of the converted word: its left edge and its baseline. */
  left: number;
  top: number;
  bottom: number;
}

export interface AlternatesState {
  /** What was typed, shown as the heading so the offer is legible. */
  roman: string;
  candidates: TransliterationCandidate[];
  anchor: AlternatesAnchor;
}

/** What a key means while the picker is open. Null means "not ours". */
export type AlternatesAction =
  | { type: 'pick'; index: number }
  | { type: 'move'; delta: number }
  | { type: 'close' };

/**
 * Maps a key to a picker action.
 *
 * Shared by both hosts so the keyboard model cannot drift between the editor and
 * a title field, where the muscle memory is the same one.
 *
 * Digits pick directly, which is what makes the picker fast: the row a writer
 * wants is nearly always visible before they have finished reading it, and
 * 2 is one keystroke where ↓↓Enter is three.
 *
 * `engaged` is what makes it safe for the list to open by itself.
 *
 * A picker somebody asked for may take the keys it needs — they are looking at
 * it. One that appeared on its own after an ambiguous word may not: the writer's
 * next keystroke is overwhelmingly the next word, and a list that swallowed
 * their Enter, or turned the `1` in `2074 salma` into a silent pick, would have
 * made typing worse in exchange for a convenience nobody asked for. Until they
 * reach for it with an arrow key, only the arrows and Escape mean anything, and
 * everything else closes it and goes through untouched.
 */
export function alternatesAction(
  event: KeyboardEvent | React.KeyboardEvent,
  count: number,
  { engaged = true }: { engaged?: boolean } = {},
): AlternatesAction | null {
  if (event.altKey || event.metaKey) return null;

  if (event.key === 'Escape') return { type: 'close' };
  if (event.key === 'ArrowDown') return { type: 'move', delta: 1 };
  if (event.key === 'ArrowUp') return { type: 'move', delta: -1 };

  if (!engaged) return null;

  if (event.key === 'Enter' || event.key === 'Tab') return { type: 'pick', index: -1 };

  if (!event.ctrlKey && /^[1-9]$/.test(event.key)) {
    const index = Number(event.key) - 1;
    return index < count ? { type: 'pick', index } : null;
  }

  return null;
}

/**
 * True when Ctrl+Space — the one chord that opens the picker — was pressed.
 *
 * `code` as well as `key`, because a layout that gives the space bar anything
 * other than a plain space still reports the physical key, and this is a chord
 * about a key position rather than about a character.
 */
export function isAlternatesTrigger(event: KeyboardEvent | React.KeyboardEvent): boolean {
  return Boolean(
    event.ctrlKey && !event.altKey && !event.metaKey && (event.key === ' ' || event.code === 'Space'),
  );
}

const WIDTH = 240;
/** Roughly the popup's height; only used to decide whether to flip it upward. */
const ESTIMATED_HEIGHT = 220;

export function AlternatesPopup({
  state,
  activeIndex,
  onPick,
  onClose,
}: {
  state: AlternatesState;
  activeIndex: number;
  onPick: (index: number) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: state.anchor.left, top: state.anchor.bottom + 6 });

  /*
   * Placed against the viewport rather than inside the editor: the writing
   * surface, the settings pages and the comment box all clip their overflow
   * differently, and a picker that is cut off by a card border is worse than no
   * picker. Measured after layout so the flip uses the real height.
   */
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? ESTIMATED_HEIGHT;
    const room = window.innerHeight - state.anchor.bottom;
    setPosition({
      left: Math.min(Math.max(8, state.anchor.left), window.innerWidth - WIDTH - 8),
      top: room < height + 16 ? Math.max(8, state.anchor.top - height - 6) : state.anchor.bottom + 6,
    });
  }, [state.anchor]);

  // Anything that moves the word out from under the popup closes it, rather than
  // leaving it pointing at a place the text no longer is.
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[60] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg"
      style={{ left: position.left, top: position.top, width: WIDTH }}
      // The field keeps the caret: clicking a row must not blur what is being
      // typed into, or the pick would land in a document that has moved on.
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className="flex items-center justify-between gap-2 border-b px-2.5 py-1.5 text-[0.7rem] text-muted-foreground">
        <code className="font-mono">{state.roman}</code>
        <span className="np">{t('छान्न १–{last}', { last: n(state.candidates.length) })}</span>
      </div>

      {/* The options are the listbox's direct children: a ul/li wrapper between
          them breaks the role relationship screen readers navigate by. */}
      <div role="listbox" aria-label={t('अरू रूप')} className="py-1">
        {state.candidates.map((candidate, index) => (
          <button
            key={candidate.text}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            onClick={() => onPick(index)}
            className={cn(
              'flex w-full items-baseline gap-2 px-2.5 py-1.5 text-start transition-colors',
              index === activeIndex ? 'bg-accent' : 'hover:bg-accent/60',
            )}
          >
            <span className="tnum w-4 shrink-0 text-[0.7rem] text-muted-foreground">{n(index + 1)}</span>
            <span className="np min-w-0 flex-1 truncate text-base">{candidate.text}</span>
            {candidate.note && (
              <span className="np shrink-0 text-[0.65rem] text-muted-foreground">
                {lang() === 'en' ? candidate.note.en : candidate.note.np}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
